<script lang="ts" setup>
import { useInput, showRequiredMark, NumberValueType, withNumberElInputProps } from '@/forms/input';
import { computed, PropType, ref, watch } from 'vue';
import ElInputContainer from '@/forms/ElInputContainer.vue';
import { DrugDurationMeasures, DrugTimeMeasures, DrugUnitMeasures } from '@/types';

interface MeasureUnitTypes {
  weight: string[];
  height: string[];
  drugUnitMeasure: string[];
  drugTimeMeasure: string[];
  drugDurationMeasure: string[];
}

const measureUnitTypes: MeasureUnitTypes = {
  weight: ['kg', 'hg', 'g'],
  height: ['m', 'cm'],
  drugUnitMeasure: Object.keys(DrugUnitMeasures),
  drugTimeMeasure: Object.keys(DrugTimeMeasures)
    .filter(measure => measure !== 'AlBisogno')
    .concat(['Al bisogno']),
  drugDurationMeasure: Object.keys(DrugDurationMeasures),
};

const props = defineProps({
  ...withNumberElInputProps(),
  availableUnitCategory: {
    type: String as PropType<keyof MeasureUnitTypes>,
    default: () => '',
  },
  defaultUnit: {
    type: String,
    default: null,
  },
  disableUnitChange: {
    type: Boolean,
    default: false,
  },
});

const availableUnits = computed(() => {
  return measureUnitTypes[props.availableUnitCategory];
});

const emit = defineEmits<{
  (event: 'update:modelValue', value: NumberValueType): void;
  (event: 'changeUnit', value: string): void;
}>();

const getDefaultUnits = (category: keyof MeasureUnitTypes) => {
  switch (category) {
    case 'drugTimeMeasure':
      return 'Giorni';
    case 'drugUnitMeasure':
      return 'Cpr';
    case 'height':
      return 'm';
    case 'weight':
      return 'kg';
    default:
      return '';
  }
};

const computedModelValue = computed(() => props.modelValue);
const computedValidation = computed(() => [props.validation]);
const currentUnit = ref<string>(props.defaultUnit || getDefaultUnits(props.availableUnitCategory));

const emitNewValue = (val: NumberValueType): void => {
  emit('update:modelValue', val);
};

const { value, errorMessage, uuid } = useInput(computedModelValue, computedValidation, props.name, newVal => emitNewValue(newVal));

const unit = ref(currentUnit);

const handleInputUnit = () => {
  const value = unit.value === 'Al bisogno' ? 'AlBisogno' : unit.value;
  emit('changeUnit', value);
};

const changeUnit = (value: string) => (unit.value = value);

watch(
  () => unit.value,
  () => {
    if (unit.value === 'Al bisogno') value.value = undefined;
  },
);

defineExpose({
  changeUnit,
});
</script>

<template>
  <ElInputContainer
    v-bind="$attrs"
    :name="uuid"
    :label="label"
    :error-message="props.errorMessage === false ? false : props.errorMessage || errorMessage"
    :hidden-error-message="hiddenErrorMessage"
    :set-required-mark="showRequiredMark([validation])"
    class="relative"
  >
    <template #default="{ error }">
      <input
        :id="uuid"
        v-model="value"
        :name="props.name"
        type="number"
        class="el-input el-input-measure-unit"
        :class="{ error }"
        :placeholder="placeholder"
        :disabled="disabled"
        autocomplete="no_thanks"
      />
      <div v-if="availableUnits && availableUnits.length > 0" class="absolute top-[4px] right-0 flex h-5/6 items-center">
        <select
          v-model="unit"
          class="mr-[-2px] max-w-[100px] rounded-md border-transparent bg-transparent py-0 pl-[0px] pr-7 text-neutral-lighter focus:border-0 focus:ring-0 sm:text-sm"
          :disabled="disableUnitChange"
          @change="handleInputUnit"
        >
          <option v-for="(item, index) in availableUnits" :key="index">
            <span>{{ item }}</span>
          </option>
        </select>
      </div>
    </template>
  </ElInputContainer>
</template>
